import React, { useState, useMemo, useEffect, useRef } from "react";
import { DEFAULT_TEXT, DEFAULT_CONFIG, getUserConfigText } from "../../config";
import ChatHistory from "../ChatHistory/ChatHistory";
import { InputField } from "../InputField/InputField";
import ChatHeader from "../ChatHeader/ChatHeader";
import "./BubbleWidget.css";
import commentSharp from "../../assets/comment-sharp-solid.svg";
import DebugInfoPanel from "../Debug/DebugInfoPanel";
import { useConfig } from "./ConfigContext";

export function calcContrastColor(mainColor) {
  // determine if white or black text will be more visible on the provided
  // main color

  // if not a hex color just use black
  if (mainColor.length !== 7) {
    return "black";
  }

  const lumi =
    parseInt(mainColor.substring(1, 3), 16) * 0.2126 +
    parseInt(mainColor.substring(3, 5), 16) * 0.7152 +
    parseInt(mainColor.substring(5, 7), 16) * 0.0722;

  // if the main color is closer to white (meaning black text would be easier
  // to read) return black, otherwise return white
  const res = lumi > 128 ? "black" : "white";
  return res;
}

/**
 * @typedef {import('../../types/config').ConfigObject} ConfigObject
 * @typedef {import('../../types/config').AdvancedSizeConfig} AdvancedSizeConfig
 */

export default function BubbleWidget({
  chatOpen,
  chatOpenMinimal,
  toggleChatOpen,
  hideChat,
  getImageUrl,
  resetConversation,
  downloadTranscript,
  panel: {
    messages,
    inboundMessage,
    setInboundMessage,
    sendMessage,
    thinking,
    adminConnected,
  },
  debugInfo: { session, wsConversationId, chatState, websocketUrl },
}) {
  const { config, colors } = useConfig();

  const [showTooltip, setShowTooltip] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false); // flag for if the user has hovered or clicked the widget
  const tooltipText = useMemo(
    () => getUserConfigText(config, "tooltip", DEFAULT_TEXT.en.tooltip),
    [document.cookie, config.overrideLanguage],
  );
  const tooltipTextColor = useMemo(
    () => calcContrastColor(colors.main),
    [colors.main],
  );
  const promptPopupText = useMemo(
    () => getUserConfigText(config, "promptPopup", DEFAULT_TEXT.en.promptPopup),
    [document.cookie],
  );

  const [showPromptPopup, setShowPromptPopup] = useState(false);

  // logic to show prompt popup after delay or immediately as appropriate
  const timerId = useRef(-1);
  useEffect(() => {
    /*
      if prompt text is defined and the user hasn't hovered or clicked the bubble
        if promptPopupTimer isn't defined or is 0
          if autoPopup is false (the panel doesn't auto open)
            set prompt open
        if promptPopupTimer is defined
          after the specified amount of time
            show the prompt
      if the user has interacted with the bubble, clear the timer if present
     */
    if (promptPopupText && !hasInteracted) {
      if (
        (config.promptPopupTimer === undefined ||
          config.promptPopupTimer <= 0) &&
        config.autoPopUp === false
      ) {
        setShowPromptPopup(true);
      } else {
        // if delay is defined and the timer hasn't previously been set, set the
        // timer (value is set in seconds so convert to ms)
        if (timerId.current === -1) {
          timerId.current = window.setTimeout(
            () => setShowPromptPopup(true),
            config.promptPopupTimer * 1000,
          );
        }
      }
    }

    if (hasInteracted) {
      setShowPromptPopup(false);
      clearTimeout(timerId.current);
    }
  }, [hasInteracted]);

  // close prompt (by setting interacted to true) when chatOpen or
  // chatOpenMinimal, need to react here since the timer for auto open is in
  // parent component
  useEffect(() => {
    if (chatOpen || chatOpenMinimal) {
      setHasInteracted(true);
    }
  }, [chatOpen, chatOpenMinimal]);

  const isDev = () => {
    // return window.location.hostname === "localhost";
    return false;
  };

  const isMagicformChatIcon = () => {
    const imageUrl = getImageUrl();
    return imageUrl && imageUrl === DEFAULT_CONFIG.imageUrl;
  };

  function calcOffsets(widgetWidth = "10rem", widgetHeight = "10rem") {
    const map = {
      // old values to keep compatibility with previous configs
      "bottom-right": {
        bubble: "bottom: 20px; right: 20px;",
        panel: "bottom: 96px; right: 20px;",
        tooltip: "bottom: 96px; right: 20px;",
        // 96 = 56 px for bubble + 20px padding on top and bottom
      },
      "bottom-left": {
        bubble: "bottom: 20px; left: 20px;",
        panel: "bottom: 96px; left: 20px;",
        tooltip: "bottom: 96px; left: 20px;",
      },
      // new values
      topLeft: {
        bubble: "top: 20px; left: 20px;",
        panel: "top: 96px; left: 20px;",
        tooltip: "top: 96px; left: 20px;",
      },
      bottomLeft: {
        bubble: "bottom: 20px; left: 20px;",
        panel: "bottom: 96px; left: 20px;",
        tooltip: "bottom: 96px; left: 20px;",
      },
      middleLeft: {
        bubble: "bottom: calc(50% - 28px); left: 20px;",
        panel: `bottom: calc(50% - (${widgetHeight} / 2)); left: 96px`,
        tooltip: `bottom: 50%; left: 96px`,
      },
      topRight: {
        bubble: "top: 20px; right: 20px;",
        panel: "top: 96px; right: 20px;",
        tooltip: "top: 96px; right: 20px;",
      },
      middleRight: {
        bubble: "bottom: calc(50% - 28px); right: 20px;",
        panel: `bottom: calc(50% - (${widgetHeight} / 2)); right: 96px`,
        tooltip: `bottom: 50%; right: 96px`,
      },
      bottomRight: {
        bubble: "bottom: 20px; right: 20px;",
        panel: "bottom: 96px; right: 20px;",
        tooltip: "bottom: 96px; right: 20px;",
      },
      topMiddle: {
        bubble: "top: 20px; right: calc(50% - 28px);",
        panel: `top: 96px; right: calc(50% - (${widgetWidth} / 2));`,
        tooltip: `top: 96px; left: 50%;`,
      },
      bottomMiddle: {
        bubble: "bottom: 20px; right: calc(50% - 28px);",
        panel: `bottom: 96px; right: calc(50% - (${widgetWidth} / 2));`,
        tooltip: `bottom: 96px; left: 50%;`,
      },
    };
    // check for old bubble position property if widgetPosition isn't set
    return map[config.widgetPosition ?? config.bubblePosition ?? "bottomRight"];
  }

  function calcAdvOffsets(widgetWidth = "10rem", widgetHeight = "10rem") {
    const map = {
      // old values to keep compatibility with previous configs
      "bottom-right": {
        bubble: "bottom: 20px; right: 20px;",
        panel: `bottom: ${config.advOffsets.panel.yOffset}; right: ${config.advOffsets.panel.xOffset};`,
        tooltip: "bottom: 96px; right: 20px;",
        // 96 = 56 px for bubble + 20px padding on top and bottom
      },
      "bottom-left": {
        bubble: "bottom: 20px; left: 20px;",
        panel: `bottom: ${config.advOffsets.panel.yOffset}; left: ${config.advOffsets.panel.xOffset}`,
        tooltip: "bottom: 96px; left: 20px;",
      },
      // new values
      topLeft: {
        bubble: "top: 20px; left: 20px;",
        panel: `top: ${config.advOffsets.panel.yOffset}; left: ${config.advOffsets.panel.xOffset}`,
        tooltip: "top: 96px; left: 20px;",
      },
      bottomLeft: {
        bubble: "bottom: 20px; left: 20px;",
        panel: `bottom: ${config.advOffsets.panel.yOffset}; left: ${config.advOffsets.panel.xOffset}`,
        tooltip: "bottom: 96px; left: 20px;",
      },
      middleLeft: {
        bubble: "bottom: calc(50% - 28px); left: 20px;",
        panel: `bottom: calc(50% - (${widgetHeight} / 2)); left: ${config.advOffsets.panel.xOffset}`,
        tooltip: `bottom: 50%; left: 96px`,
      },
      topRight: {
        bubble: "top: 20px; right: 20px;",
        panel: `top: ${config.advOffsets.panel.yOffset}; right: ${config.advOffsets.panel.xOffset}`,
        tooltip: "top: 96px; right: 20px;",
      },
      middleRight: {
        bubble: "bottom: calc(50% - 28px); right: 20px;",
        panel: `bottom: calc(50% - (${widgetHeight} / 2)); right: ${config.advOffsets.panel.xOffset}`,
        tooltip: `bottom: 50%; right: 96px`,
      },
      bottomRight: {
        bubble: "bottom: 20px; right: 20px;",
        panel: `bottom: ${config.advOffsets.panel.yOffset}; right: ${config.advOffsets.panel.xOffset}`,
        tooltip: "bottom: 96px; right: 20px;",
      },
      topMiddle: {
        bubble: "top: 20px; right: calc(50% - 28px);",
        panel: `top: ${config.advOffsets.panel.yOffset}; right: calc(50% - (${widgetWidth} / 2));`,
        tooltip: `top: 96px; left: 50%;`,
      },
      bottomMiddle: {
        bubble: "bottom: 20px; right: calc(50% - 28px);",
        panel: `bottom: ${config.advOffsets.panel.yOffset}; right: calc(50% - (${widgetWidth} / 2));`,
        tooltip: `bottom: 96px; left: 50%;`,
      },
    };
    // check for old bubble position property if widgetPosition isn't set
    return map[config.widgetPosition ?? config.bubblePosition ?? "bottomRight"];
  }

  function handleBubbleMouseEnter() {
    if (!(chatOpen || chatOpenMinimal)) {
      setHasInteracted(true);
      setShowPromptPopup(false);
      setShowTooltip(true);
    }
  }

  function handleBubbleClick() {
    setHasInteracted(true);
    setShowPromptPopup(false);
    setShowTooltip(false);
    toggleChatOpen();
  }

  return (
    <>
      <style>
        {`
          .iconHover:hover {
              transform: scale(1.1);
          }
          .bubbleOffset {
            ${calcOffsets().bubble}
          }
        `}
      </style>
      {config?.useAdvSizes ? (
        <style>
          {`

        @media screen and (max-width:${config?.advSizes?.small.breakPoint}px) {
          .fullScreenHideIfChatOpen {
            display: ${chatOpen ? "none" : "flex"}
          }
          .needsDimensions {
            height: 100%;
            width: 100%;
          }
          .panelOffset {
            top:0;
            left: 0;
            right: 0;
          }
          .tooltipOffset {
            ${
              calcOffsets(
                config.advSizes?.small.width,
                config.advSizes?.small.height,
              ).tooltip
            }
          }
        }
        @media screen and (min-width:${config?.advSizes?.small.breakPoint}px) {
          .fullScreenHideIfChatOpen {
            display: flex;
          }
          .needsDimensions {
            width: ${config.advSizes?.small.width};
            height: ${config.advSizes?.small.height};
          }
          .panelOffset {
            ${
              config.useAdvOffsets
                ? calcAdvOffsets(
                    config.advSizes?.small.width,
                    config.advSizes?.small.height,
                  ).panel
                : calcOffsets(
                    config.advSizes?.small.width,
                    config.advSizes?.small.height,
                  ).panel
            }
          }
          .tooltipOffset {
            ${
              calcOffsets(
                config.advSizes?.small.width,
                config.advSizes?.small.height,
              ).tooltip
            }
          }
        }
        @media screen and (min-width:${config?.advSizes?.medium.breakPoint}px) {
          .needsDimensions {
            width: ${config.advSizes?.medium.width};
            height: ${config.advSizes?.medium.height};
          }
          .panelOffset {
            ${
              config.useAdvOffsets
                ? calcAdvOffsets(
                    config.advSizes?.medium.width,
                    config.advSizes?.medium.height,
                  ).panel
                : calcOffsets(
                    config.advSizes?.medium.width,
                    config.advSizes?.medium.height,
                  ).panel
            }
          }
          .tooltipOffset {
            ${
              calcOffsets(
                config.advSizes?.medium.width,
                config.advSizes?.medium.height,
              ).tooltip
            }
          }
        }
        @media screen and (min-width:${config?.advSizes?.large.breakPoint}px) {
          .needsDimensions {
            width: ${config.advSizes?.large.width};
            height: ${config.advSizes?.large.height};
          }
          .panelOffset {
            ${
              config.useAdvOffsets
                ? calcAdvOffsets(
                    config.advSizes?.large.width,
                    config.advSizes?.large.height,
                  ).panel
                : calcOffsets(
                    config.advSizes?.large.width,
                    config.advSizes?.large.height,
                  ).panel
            }
          }
          .tooltipOffset {
            ${
              calcOffsets(
                config.advSizes?.large.width,
                config.advSizes?.large.height,
              ).tooltip
            }
          }
        }
        `}
        </style>
      ) : (
        <style>
          {`
          .fullScreenHideIfChatOpen {
            display: flex;
          }
          .needsDimensions {
            height: ${config.chatHeight ?? DEFAULT_CONFIG.chatHeight};
            width: ${config.chatWidth ?? DEFAULT_CONFIG.chatWidth};
          }
          .tooltipOffset {
            ${
              calcOffsets(
                config.chatWidth ?? DEFAULT_CONFIG.chatWidth,
                config.chatHeight ?? DEFAULT_CONFIG.chatHeight,
              ).tooltip
            }
          }
          .panelOffset {
            ${
              config.useAdvOffsets
                ? calcAdvOffsets(
                    config.chatWidth ?? DEFAULT_CONFIG.chatWidth,
                    config.chatHeight ?? DEFAULT_CONFIG.chatHeight,
                  ).panel
                : calcOffsets(
                    config.chatWidth ?? DEFAULT_CONFIG.chatWidth,
                    config.chatHeight ?? DEFAULT_CONFIG.chatHeight,
                  ).panel
            }
          }
        `}
        </style>
      )}

      {/* Prompt (click me box) */}
      {promptPopupText && (
        <div
          className="tooltipOffset" // inherits offset from tooltip
          dir="auto"
          style={{
            position: "fixed",
            backgroundColor: colors.main,
            fontFamily: "sans-serif",
            fontSize: config.promptPopupFontSize,
            color: tooltipTextColor, // inherits tooltip color for now
            borderRadius: "8px",
            boxShadow: "0 5px 10px rgb(0 0 0 / 0.3)",
            visibility: showPromptPopup ? "visible" : "hidden",
            opacity: showPromptPopup ? 1 : 0,
            transition: "visibility 0.1s linear,opacity 0.1s linear",
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
            paddingTop: "0.5rem",
            paddingBottom: "0.5rem",
            maxWidth: "20rem",
            zIndex: 5,
          }}
        >
          {promptPopupText}
        </div>
      )}

      {/* Tooltip */}
      {tooltipText && (
        <div
          className="tooltipOffset"
          dir="auto"
          style={{
            position: "fixed",
            backgroundColor: colors.main,
            fontFamily: "sans-serif",
            fontSize: config.tooltipFontSize,
            color: tooltipTextColor,
            borderRadius: "8px",
            boxShadow: "0 5px 10px rgb(0 0 0 / 0.3)",
            visibility: showTooltip ? "visible" : "hidden",
            opacity: showTooltip ? 1 : 0,
            transition: "visibility 0.1s linear,opacity 0.1s linear",
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
            paddingTop: "0.5rem",
            paddingBottom: "0.5rem",
            maxWidth: "20rem",
            zIndex: 6,
          }}
        >
          {tooltipText}
        </div>
      )}

      {/*Chat Icon*/}
      <div
        className={`magicform-chat-icon fullScreenHideIfChatOpen bubbleOffset iconHover`}
        style={{
          position: "fixed",
          backgroundColor: colors.main,
          width: "56px",
          height: "56px",
          textAlign: "center",
          lineHeight: "3.5rem",
          fontSize: "2rem",
          cursor: "pointer",
          borderRadius: "5rem",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "auto",
          transition: "transform 0.3s ease",
          display:
            config?.hideBubbleWhenPanelOpen === true && chatOpen
              ? "none"
              : "flex",
        }}
        onClick={() => handleBubbleClick()}
        onMouseEnter={() => handleBubbleMouseEnter()}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {commentSharp ? (
          <img
            className="magicform-chat-icon-content"
            src={getImageUrl()}
            alt="Comments Icon"
            width="30px"
            height="30px"
            style={{
              marginTop: "4px",
              marginBottom: "3px",
              marginLeft: "3px",
              marginRight: "4px",
              filter: isMagicformChatIcon() ? "invert(100%)" : "none",
            }}
          />
        ) : (
          <p className="magicform-chat-icon-content">Chat</p>
        )}
      </div>

      {/* Chat Panel */}
      {chatOpenMinimal ? (
        <>
          <div
            id="magicform-bubble-widget-chat-panel-minimal"
            style={{
              zIndex: 100,
              display: chatOpen ? "flex" : "none",
              borderRadius: "1rem",
              flexDirection: "column",
              pointerEvents: "auto",
            }}
            className={`needsHeight`}
          >
            <ChatHistory
              messages={messages}
              thinking={thinking}
              minimal={chatOpenMinimal}
              toggleChatOpen={toggleChatOpen}
              hideChat={hideChat}
            />
          </div>
        </>
      ) : (
        <div
          id="magicform-bubble-widget-chat-panel"
          style={{
            display: chatOpen ? "flex" : "none",
            borderRadius: "1rem",
            boxShadow: "0 5px 10px rgb(0 0 0 / 0.3)",
            backgroundColor: "white",
            flexDirection: "column",
            pointerEvents: "auto",
            position: "fixed",
            zIndex: 100,
          }}
          className={`needsDimensions panelOffset`}
        >
          <ChatHeader
            toggleChatOpen={toggleChatOpen}
            resetConversation={resetConversation}
            adminConnected={adminConnected}
            downloadTranscript={downloadTranscript}
          />
          {isDev() ? (
            <DebugInfoPanel
              session={session}
              wsConversationId={wsConversationId}
              chatState={chatState}
              websocketUrl={websocketUrl}
            />
          ) : (
            <></>
          )}
          <ChatHistory
            messages={messages}
            thinking={thinking}
            minimal={false}
          />
          <InputField
            inboundMessage={inboundMessage}
            setInboundMessage={setInboundMessage}
            onKeyEnter={sendMessage}
            isLoading={thinking}
          />
        </div>
      )}
    </>
  );
}
